import { MarketingLaunchPanel } from '../launches/MarketingLaunchPanel';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  AlertTriangle,
  CalendarClock,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Mail,
  Plus,
  RefreshCw,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { EmptyState } from '../../../components/EmptyState';
import { Alert } from '../../../components/ui/Alert';
import { Badge, type BadgeVariant } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { FormField } from '../../../components/ui/FormField';
import { Input } from '../../../components/ui/Input';
import { Modal } from '../../../components/ui/Modal';
import { Select } from '../../../components/ui/Select';
import {
  marketingApi,
  type MarketingBroadcast,
  type MarketingBroadcastScheduleInput,
  type MarketingBroadcastStatus,
  type MarketingMessageSummary,
  type MarketingSegmentSummary,
} from '../../../lib/api';
import {
  buildMarketingMonthCalendar,
  createDefaultMarketingBroadcastDate,
  formatMarketingCalendarDateKey,
  marketingDateTimeLocalToIso,
  marketingIsoToDateTimeLocal,
  marketingMonthCalendarRange,
} from './marketing-broadcast-calendar';

const WEEKDAYS = ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Niedz'];
const MONTH_FORMATTER = new Intl.DateTimeFormat('pl-PL', { month: 'long', year: 'numeric' });
const DATE_FORMATTER = new Intl.DateTimeFormat('pl-PL', { dateStyle: 'medium', timeStyle: 'short' });
const TIME_FORMATTER = new Intl.DateTimeFormat('pl-PL', { hour: '2-digit', minute: '2-digit' });

const STATUS_LABELS: Record<MarketingBroadcastStatus, string> = {
  DRAFT: 'Szkic',
  SCHEDULED: 'Zaplanowana',
  CANCELLED: 'Anulowana',
  COMPLETED: 'Zakończona',
  FAILED: 'Błąd',
};

const STATUS_VARIANTS: Record<MarketingBroadcastStatus, BadgeVariant> = {
  DRAFT: 'neutral',
  SCHEDULED: 'accent',
  CANCELLED: 'neutral',
  COMPLETED: 'success',
  FAILED: 'danger',
};

type ScheduleEditorState =
  | { mode: 'create'; broadcast: null }
  | { mode: 'reschedule'; broadcast: MarketingBroadcast };

function apiErrorMessage(error: unknown): string {
  if (isAxiosError<{ error?: string }>(error)) {
    if (error.response?.data?.error) return error.response.data.error;
    if (error.response?.status === 404) return 'Backend nie udostępnia jeszcze harmonogramu wysyłek.';
    if (error.response?.status === 503) return 'Aktualizacja bazy danych nie została jeszcze zakończona.';
  }
  return 'Nie udało się wykonać operacji. Spróbuj ponownie.';
}

function versionChoice(id: string, versionNumber: number): string {
  return `${id}:${versionNumber}`;
}

function parseVersionChoice(choice: string): { id: string; versionNumber: number } | null {
  const separator = choice.lastIndexOf(':');
  const versionNumber = Number(choice.slice(separator + 1));
  if (separator < 1 || !Number.isInteger(versionNumber) || versionNumber < 1) return null;
  return { id: choice.slice(0, separator), versionNumber };
}

function uniqueVersionOptions(
  summaries: Array<MarketingMessageSummary | MarketingSegmentSummary>,
  current: { id: string; versionNumber: number; label: string } | null,
): Array<{ value: string; label: string }> {
  const options = summaries
    .filter(summary => summary.status !== 'ARCHIVED')
    .map(summary => ({
      value: versionChoice(summary.id, summary.latestVersionNumber),
      label: `${summary.name} - v${summary.latestVersionNumber}`,
    }));
  if (current) {
    const currentValue = versionChoice(current.id, current.versionNumber);
    if (!options.some(option => option.value === currentValue)) {
      options.unshift({ value: currentValue, label: `${current.label} - v${current.versionNumber} (obecnie)` });
    }
  }
  return options;
}

function BroadcastScheduleModal({
  editor,
  messages,
  segments,
  isPending,
  error,
  onClose,
  onSubmit,
}: {
  editor: ScheduleEditorState | null;
  messages: MarketingMessageSummary[];
  segments: MarketingSegmentSummary[];
  isPending: boolean;
  error: unknown;
  onClose: () => void;
  onSubmit: (input: MarketingBroadcastScheduleInput) => void;
}) {
  const [name, setName] = useState('');
  const [messageChoice, setMessageChoice] = useState('');
  const [segmentChoice, setSegmentChoice] = useState('');
  const [scheduledFor, setScheduledFor] = useState('');
  const [validationError, setValidationError] = useState('');
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Warsaw';
  const currentVersion = editor?.broadcast?.scheduledVersion ?? null;
  const messageOptions = useMemo(() => uniqueVersionOptions(messages, currentVersion ? {
    id: currentVersion.messageId,
    versionNumber: currentVersion.messageVersionNumber,
    label: currentVersion.messageName,
  } : null), [currentVersion, messages]);
  const segmentOptions = useMemo(() => uniqueVersionOptions(segments, currentVersion ? {
    id: currentVersion.segmentId,
    versionNumber: currentVersion.segmentVersionNumber,
    label: currentVersion.segmentName,
  } : null), [currentVersion, segments]);

  useEffect(() => {
    if (!editor) return;
    setName(editor.broadcast?.name ?? '');
    setMessageChoice(currentVersion
      ? versionChoice(currentVersion.messageId, currentVersion.messageVersionNumber)
      : messageOptions[0]?.value ?? '');
    setSegmentChoice(currentVersion
      ? versionChoice(currentVersion.segmentId, currentVersion.segmentVersionNumber)
      : segmentOptions[0]?.value ?? '');
    setScheduledFor(currentVersion
      ? marketingIsoToDateTimeLocal(currentVersion.scheduledFor)
      : createDefaultMarketingBroadcastDate());
    setValidationError('');
  }, [currentVersion, editor, messageOptions, segmentOptions]);

  const submit = () => {
    const message = parseVersionChoice(messageChoice);
    const segment = parseVersionChoice(segmentChoice);
    const scheduledForIso = marketingDateTimeLocalToIso(scheduledFor);
    if (!name.trim()) return setValidationError('Podaj nazwę wysyłki.');
    if (!message) return setValidationError('Wybierz gotową wiadomość.');
    if (!segment) return setValidationError('Wybierz segment odbiorców.');
    if (!scheduledForIso) return setValidationError('Wybierz prawidłowy termin wysyłki.');
    if (new Date(scheduledForIso).getTime() < Date.now() + 5 * 60 * 1000) {
      return setValidationError('Termin musi być późniejszy o co najmniej 5 minut.');
    }
    setValidationError('');
    onSubmit({
      name: name.trim(),
      messageId: message.id,
      messageVersionNumber: message.versionNumber,
      segmentId: segment.id,
      segmentVersionNumber: segment.versionNumber,
      scheduledFor: scheduledForIso,
      timezone: browserTimezone,
    });
  };

  const missingResources = messageOptions.length === 0 || segmentOptions.length === 0;
  return (
    <Modal
      isOpen={Boolean(editor)}
      onClose={onClose}
      closeOnBackdrop={!isPending}
      closeOnEscape={!isPending}
      hideCloseButton={isPending}
      title={editor?.mode === 'reschedule' ? 'Przeplanuj wysyłkę' : 'Zaplanuj wysyłkę'}
      description="Termin zostanie zapisany w kalendarzu. Dispatch pozostaje zablokowany do czasu przejścia pre-flight i aktywacji providera."
      size="lg"
      footer={(
        <div className="flex items-center justify-between gap-3">
          <Button
            onClick={submit}
            loading={isPending}
            disabled={missingResources}
            leftIcon={<CalendarClock />}
          >
            {editor?.mode === 'reschedule' ? 'Zapisz nowy termin' : 'Zaplanuj wysyłkę'}
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={isPending}>Anuluj</Button>
        </div>
      )}
    >
      <div className="space-y-4">
        {missingResources && (
          <Alert variant="warning" title="Najpierw przygotuj treść i odbiorców">
            Potrzebna jest co najmniej jedna aktywna wiadomość z renderem oraz jeden aktywny segment.
            <div className="mt-2 flex flex-wrap gap-3 text-xs">
              <Link className="font-medium text-accent-primary hover:underline" to="/marketing/email/wiadomosci">Otwórz wiadomości</Link>
              <Link className="font-medium text-accent-primary hover:underline" to="/marketing/email/odbiorcy">Otwórz odbiorców</Link>
            </div>
          </Alert>
        )}

        <FormField label="Nazwa wysyłki" htmlFor="broadcast-name" required>
          <Input
            id="broadcast-name"
            value={name}
            maxLength={120}
            onChange={event => setName(event.target.value)}
            placeholder="np. Premiera kolekcji - wrzesień"
            autoFocus
          />
        </FormField>

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label="Wiadomość" htmlFor="broadcast-message" required helper="Wysyłka zostanie przypięta do dokładnej wersji.">
            <Select id="broadcast-message" value={messageChoice} onChange={event => setMessageChoice(event.target.value)}>
              {messageOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </Select>
          </FormField>
          <FormField label="Segment" htmlFor="broadcast-segment" required helper="Członkostwo i eligibility będą sprawdzone ponownie przed wysyłką.">
            <Select id="broadcast-segment" value={segmentChoice} onChange={event => setSegmentChoice(event.target.value)}>
              {segmentOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </Select>
          </FormField>
        </div>

        <FormField
          label="Termin"
          htmlFor="broadcast-scheduled-for"
          required
          helper={`Strefa operatora: ${browserTimezone}. Minimalne wyprzedzenie: 5 minut.`}
        >
          <Input
            id="broadcast-scheduled-for"
            type="datetime-local"
            value={scheduledFor}
            onChange={event => setScheduledFor(event.target.value)}
          />
        </FormField>

        {Boolean(validationError || error) && (
          <Alert variant="danger" title="Nie udało się zapisać harmonogramu">
            {validationError || apiErrorMessage(error)}
          </Alert>
        )}
      </div>
    </Modal>
  );
}

function BroadcastCard({
  broadcast,
  onReschedule,
  onCancel,
}: {
  broadcast: MarketingBroadcast;
  onReschedule: () => void;
  onCancel: () => void;
}) {
  const version = broadcast.scheduledVersion;
  const estimate = broadcast.scheduleEvents[0];
  return (
    <Card padding="sm" className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold text-text-primary">{broadcast.name}</h3>
          <p className="mt-1 text-xs text-text-muted">
            {broadcast.scheduledFor ? DATE_FORMATTER.format(new Date(broadcast.scheduledFor)) : 'Bez terminu'}
          </p>
        </div>
        <Badge size="sm" variant={STATUS_VARIANTS[broadcast.status]}>{STATUS_LABELS[broadcast.status]}</Badge>
      </div>
      {version && (
        <div className="space-y-1 text-xs text-text-secondary">
          <p className="truncate"><span className="text-text-muted">Wiadomość:</span> {version.messageName} v{version.messageVersionNumber}</p>
          <p className="truncate"><span className="text-text-muted">Segment:</span> {version.segmentName} v{version.segmentVersionNumber}</p>
          {estimate?.contactsWithConsent != null && (
            <p><span className="text-text-muted">Ze zgodą przy planowaniu:</span> {estimate.contactsWithConsent}</p>
          )}
          {estimate?.preflightDecision?.passed && (
            <p className="inline-flex items-center gap-1 text-success">
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
              Publish-time pre-flight: PASS
            </p>
          )}
        </div>
      )}
      {broadcast.status === 'SCHEDULED' && (
        <div className="flex items-center justify-between gap-2 border-t border-border-subtle pt-3">
          <Button size="sm" variant="ghost" onClick={onReschedule} leftIcon={<RefreshCw />}>Przeplanuj</Button>
          <Button size="sm" variant="ghost" className="text-danger hover:text-danger" onClick={onCancel}>Anuluj</Button>
        </div>
      )}
    </Card>
  );
}

export function MarketingBroadcastPanel() {
  const queryClient = useQueryClient();
  const [brandId, setBrandId] = useState('');
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const [editor, setEditor] = useState<ScheduleEditorState | null>(null);
  const [cancelTarget, setCancelTarget] = useState<MarketingBroadcast | null>(null);
  const calendarDays = useMemo(() => buildMarketingMonthCalendar(calendarMonth), [calendarMonth]);
  const range = useMemo(() => marketingMonthCalendarRange(calendarMonth), [calendarMonth]);

  const brandsQuery = useQuery({
    queryKey: ['marketing-brands'],
    queryFn: async () => (await marketingApi.listBrands()).data,
    staleTime: 60_000,
  });
  const brands = brandsQuery.data ?? [];
  const selectedBrandId = brands.some(brand => brand.id === brandId) ? brandId : brands[0]?.id ?? '';
  const messagesQuery = useQuery({
    queryKey: ['marketing-messages', selectedBrandId],
    queryFn: async () => (await marketingApi.listMessages(selectedBrandId)).data,
    enabled: Boolean(selectedBrandId),
  });
  const segmentsQuery = useQuery({
    queryKey: ['marketing-segments', selectedBrandId],
    queryFn: async () => (await marketingApi.listSegments(selectedBrandId)).data,
    enabled: Boolean(selectedBrandId),
  });
  const broadcastsQuery = useQuery({
    queryKey: ['marketing-broadcasts', selectedBrandId, range.from, range.to],
    queryFn: async () => (await marketingApi.listBroadcasts(selectedBrandId, range.from, range.to)).data,
    enabled: Boolean(selectedBrandId),
  });

  const invalidateBroadcasts = async () => {
    await queryClient.invalidateQueries({ queryKey: ['marketing-broadcasts', selectedBrandId] });
  };
  const createMutation = useMutation({
    mutationFn: (input: MarketingBroadcastScheduleInput) => marketingApi.createScheduledBroadcast(selectedBrandId, input),
    onSuccess: async () => {
      setEditor(null);
      await invalidateBroadcasts();
    },
  });
  const rescheduleMutation = useMutation({
    mutationFn: ({ broadcast, input }: { broadcast: MarketingBroadcast; input: MarketingBroadcastScheduleInput }) => (
      marketingApi.rescheduleBroadcast(selectedBrandId, broadcast.id, {
        ...input,
        expectedScheduledVersionNumber: broadcast.scheduledVersionNumber!,
      })
    ),
    onSuccess: async () => {
      setEditor(null);
      await invalidateBroadcasts();
    },
  });
  const cancelMutation = useMutation({
    mutationFn: (broadcast: MarketingBroadcast) => marketingApi.cancelBroadcast(selectedBrandId, broadcast.id, {
      expectedScheduledVersionNumber: broadcast.scheduledVersionNumber!,
      reason: 'Anulowano w kalendarzu Marketingu',
    }),
    onSuccess: async () => {
      setCancelTarget(null);
      await invalidateBroadcasts();
    },
  });

  if (brandsQuery.isLoading) {
    return <div className="min-h-48 animate-pulse rounded-card border border-border-subtle bg-surface-raised/40" aria-label="Ładowanie kalendarza" />;
  }
  if (brandsQuery.isError) {
    return <EmptyState icon={AlertTriangle} title="Nie udało się pobrać marek" description={apiErrorMessage(brandsQuery.error)} buttonLabel="Spróbuj ponownie" onButtonClick={() => void brandsQuery.refetch()} />;
  }
  if (!selectedBrandId) {
    return <EmptyState icon={CalendarDays} title="Brak przestrzeni marki" description="Utwórz sklep lub MarketingBrand, aby planować wysyłki email." />;
  }

  const broadcasts = broadcastsQuery.data ?? [];
  const broadcastsByDay = new Map<string, MarketingBroadcast[]>();
  for (const broadcast of broadcasts) {
    if (!broadcast.scheduledFor) continue;
    const key = formatMarketingCalendarDateKey(new Date(broadcast.scheduledFor));
    broadcastsByDay.set(key, [...(broadcastsByDay.get(key) ?? []), broadcast]);
  }
  const scheduled = broadcasts.filter(broadcast => broadcast.status === 'SCHEDULED');
  const cancelled = broadcasts.filter(broadcast => broadcast.status === 'CANCELLED');
  const consentEstimate = scheduled.reduce((sum, broadcast) => (
    sum + (broadcast.scheduleEvents[0]?.contactsWithConsent ?? 0)
  ), 0);
  const schedulePending = createMutation.isPending || rescheduleMutation.isPending;
  const scheduleError = editor?.mode === 'reschedule' ? rescheduleMutation.error : createMutation.error;

  return (
    <section className="space-y-4" aria-label="Wysyłki i kalendarz">
      <Alert variant="info" title="Pre-flight działa, dispatch pozostaje wyłączony">
        Każde planowanie sprawdza dokładną wersję treści, render, segment i zakres marki. System nadal nie tworzy jobów wysyłkowych, dopóki provider, delivery runtime i globalne bramki nie zostaną jawnie aktywowane.
      </Alert>

      <div className="flex flex-col gap-3 rounded-card border border-border-subtle bg-surface-raised/40 p-3 sm:flex-row sm:items-end sm:justify-between">
        <FormField label="Przestrzeń marki" htmlFor="marketing-broadcast-brand" className="w-full sm:w-72">
          <Select id="marketing-broadcast-brand" value={selectedBrandId} onChange={event => setBrandId(event.target.value)}>
            {brands.map(brand => <option key={brand.id} value={brand.id}>{brand.name}</option>)}
          </Select>
        </FormField>
        <Button onClick={() => setEditor({ mode: 'create', broadcast: null })} leftIcon={<Plus />}>Zaplanuj wysyłkę</Button>
      </div>

      <MarketingLaunchPanel key={selectedBrandId} brandId={selectedBrandId} range={range} broadcasts={broadcasts} />

      <div className="grid gap-3 sm:grid-cols-3">
        <Card padding="sm" className="flex items-center gap-3">
          <CalendarClock className="h-5 w-5 text-accent-primary" aria-hidden="true" />
          <div><p className="text-lg font-semibold text-text-primary">{scheduled.length}</p><p className="text-xs text-text-muted">zaplanowanych w widoku</p></div>
        </Card>
        <Card padding="sm" className="flex items-center gap-3">
          <Users className="h-5 w-5 text-accent-primary" aria-hidden="true" />
          <div><p className="text-lg font-semibold text-text-primary">{consentEstimate}</p><p className="text-xs text-text-muted">ze zgodą przy planowaniu</p></div>
        </Card>
        <Card padding="sm" className="flex items-center gap-3">
          <Clock3 className="h-5 w-5 text-text-muted" aria-hidden="true" />
          <div><p className="text-lg font-semibold text-text-primary">{cancelled.length}</p><p className="text-xs text-text-muted">anulowanych w widoku</p></div>
        </Card>
      </div>

      <Card padding="none" className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border-subtle p-4">
          <div className="flex items-center gap-2">
            <Button variant="icon" size="sm" aria-label="Poprzedni miesiąc" leftIcon={<ChevronLeft />} onClick={() => setCalendarMonth(month => new Date(month.getFullYear(), month.getMonth() - 1, 1))} />
            <Button variant="secondary" size="sm" onClick={() => setCalendarMonth(new Date())}>Dzisiaj</Button>
            <Button variant="icon" size="sm" aria-label="Następny miesiąc" leftIcon={<ChevronRight />} onClick={() => setCalendarMonth(month => new Date(month.getFullYear(), month.getMonth() + 1, 1))} />
          </div>
          <h2 className="text-sm font-semibold capitalize text-text-primary">{MONTH_FORMATTER.format(calendarMonth)}</h2>
        </div>

        {broadcastsQuery.isLoading ? (
          <div className="min-h-96 animate-pulse bg-surface-raised/30" aria-label="Ładowanie wysyłek" />
        ) : broadcastsQuery.isError ? (
          <div className="p-6">
            <EmptyState icon={AlertTriangle} title="Nie udało się pobrać kalendarza" description={apiErrorMessage(broadcastsQuery.error)} buttonLabel="Spróbuj ponownie" onButtonClick={() => void broadcastsQuery.refetch()} />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[760px]">
              <div className="grid grid-cols-7 border-b border-border-subtle bg-surface-base/30">
                {WEEKDAYS.map(day => <div key={day} className="px-2 py-2 text-center text-xxs font-medium uppercase tracking-wide text-text-muted">{day}</div>)}
              </div>
              <div className="grid grid-cols-7">
                {calendarDays.map(day => {
                  const items = broadcastsByDay.get(day.key) ?? [];
                  const isToday = day.key === formatMarketingCalendarDateKey(new Date());
                  return (
                    <div key={day.key} className="min-h-28 border-b border-r border-border-subtle p-2 last:border-r-0">
                      <div className="mb-2 flex items-center justify-between">
                        <span className={[
                          'inline-flex h-6 min-w-6 items-center justify-center rounded-badge px-1 text-xs',
                          isToday ? 'bg-accent-primary text-white' : day.inCurrentMonth ? 'text-text-secondary' : 'text-text-disabled',
                        ].join(' ')}>{day.date.getDate()}</span>
                      </div>
                      <div className="space-y-1">
                        {items.slice(0, 3).map(broadcast => (
                          <button
                            key={broadcast.id}
                            type="button"
                            disabled={broadcast.status !== 'SCHEDULED'}
                            onClick={() => setEditor({ mode: 'reschedule', broadcast })}
                            className={[
                              'block w-full truncate rounded-md border px-2 py-1 text-left text-xxs transition-colors',
                              broadcast.status === 'SCHEDULED'
                                ? 'border-accent-primary/30 bg-accent-primary/10 text-accent-primary hover:bg-accent-primary/15'
                                : 'border-border-subtle bg-surface-muted/30 text-text-disabled line-through',
                            ].join(' ')}
                            title={broadcast.name}
                          >
                            {broadcast.scheduledFor && <span className="mr-1">{TIME_FORMATTER.format(new Date(broadcast.scheduledFor))}</span>}
                            {broadcast.name}
                          </button>
                        ))}
                        {items.length > 3 && <p className="px-1 text-xxs text-text-muted">+{items.length - 3} więcej</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </Card>

      <div>
        <div className="mb-3 flex items-center gap-2">
          <Mail className="h-4 w-4 text-accent-primary" aria-hidden="true" />
          <h2 className="text-sm font-semibold text-text-primary">Wysyłki w bieżącym widoku</h2>
        </div>
        {broadcasts.length === 0 && !broadcastsQuery.isLoading ? (
          <EmptyState
            icon={CalendarDays}
            size="sm"
            title="Brak wysyłek w tym miesiącu"
            description="Zaplanuj pierwszą jednorazową wysyłkę. Jej dispatch pozostanie zablokowany do czasu pre-flight."
            buttonLabel="Zaplanuj wysyłkę"
            onButtonClick={() => setEditor({ mode: 'create', broadcast: null })}
          />
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {broadcasts.map(broadcast => (
              <BroadcastCard
                key={broadcast.id}
                broadcast={broadcast}
                onReschedule={() => setEditor({ mode: 'reschedule', broadcast })}
                onCancel={() => setCancelTarget(broadcast)}
              />
            ))}
          </div>
        )}
      </div>

      <BroadcastScheduleModal
        editor={editor}
        messages={messagesQuery.data ?? []}
        segments={segmentsQuery.data ?? []}
        isPending={schedulePending}
        error={scheduleError}
        onClose={() => !schedulePending && setEditor(null)}
        onSubmit={input => {
          if (editor?.mode === 'reschedule') {
            rescheduleMutation.mutate({ broadcast: editor.broadcast, input });
          } else {
            createMutation.mutate(input);
          }
        }}
      />

      <ConfirmDialog
        isOpen={Boolean(cancelTarget)}
        title="Anulować zaplanowaną wysyłkę?"
        subtitle="Historia harmonogramu pozostanie w audycie."
        message={(
          <div className="space-y-3">
            <p>{cancelTarget ? `Wysyłka „${cancelTarget.name}” zostanie oznaczona jako anulowana.` : ''}</p>
            {cancelMutation.isError && (
              <p className="text-xs text-danger" role="alert">{apiErrorMessage(cancelMutation.error)}</p>
            )}
          </div>
        )}
        description="Ta operacja nie usuwa wiadomości ani segmentu i nie tworzy żadnego joba wysyłkowego."
        confirmLabel="Anuluj wysyłkę"
        danger
        isPending={cancelMutation.isPending}
        onConfirm={() => cancelTarget && cancelMutation.mutate(cancelTarget)}
        onClose={() => !cancelMutation.isPending && setCancelTarget(null)}
      />
    </section>
  );
}
